import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import t from 'react-translate';
import { openNovoAIDialog } from 'redux/actions/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import NvIcon from 'shared/components/nv-icon';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { getCurrentCourse } from 'redux/selectors/course';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import SendMessageModal from 'shared/components/nv-community-completion/send-message-modal';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { AdditionalResourceStatus } from 'redux/schemas/models/help-options';
import NvTooltip from 'shared/components/nv-tooltip';
import { RootState } from 'redux/schemas';
import { useCourseOutline } from 'redux/selectors/course-outline/use-course-outline';
import useTrapFocus from 'shared/hooks/use-trap-focus';
import {
  askNovoAiContainer,
  novoAiCollapsedWrapper,
  novoAiExpandedWrapper,
} from './styles';
import InitialDrawerState from './components/initial-drawer-state';
import LoadingDrawerState from './components/loading-drawer-state';
import ResponseDrawerState from './components/response-drawer-state';
import { AskNovoAiDrawerState, AskNovoAiState } from './helpers';
import ErrorDrawerState from './components/error-drawer-state';
import useAskNovoAiStream, { LecturePageTitleMap } from './use-ask-novo-ai-stream';

export type DisplayMode = 'full' | 'icon';

type Props = {
  displayMode?: DisplayMode;
  containerClassName?: string;
};

const AskNovoAI: React.FC<Props> = ({ displayMode = 'full', containerClassName }) => {
  const [formFields, setFormFields] = React.useState<string>('');
  const [showSendMessage, setSendMessage] = React.useState<boolean>(false);
  const expandedDrawerRef = useRef<HTMLDivElement>(null);
  const askNovoAiButtonRef = useRef<HTMLButtonElement>(null);
  const { lecturePageId, catalogId } = useLecturePageParams();
  const courseOutline = useCourseOutline(catalogId);
  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentUser);
  const course = useSelector(getCurrentCourse);
  const { hasAiLearningAssistant } = useSelector(getCurrentInstitution);
  const lecturePagesById = useSelector((state: RootState) => state.models.lecturePages);

  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const { helpOptions } = askNovoAi;
  const shouldShowNovoAi = hasAiLearningAssistant && (helpOptions?.hasAiLearningAssistantEnabled || helpOptions?.hasAiEmbeddingInProgress) && !course.inArchiveMode;
  const isLecturePage = !!lecturePageId;
  const isProcessingFilesInProgress = helpOptions?.additionalResources?.some(resource => resource.status === AdditionalResourceStatus.PROCESSING);
  const lecturePageTitleById = React.useMemo(() => {
    const titleMap: LecturePageTitleMap = new Map();

    courseOutline.forEach((item) => {
      if (item.type === 'lecture' && item.name) {
        titleMap.set(String(item.id), item.name);
      }
    });

    Object.keys(lecturePagesById).forEach((lecturePageKey) => {
      const lecturePage = lecturePagesById[lecturePageKey];
      const title = lecturePage?.translatedTitle || lecturePage?.title;
      if (lecturePage?.id && title && !titleMap.has(String(lecturePage.id))) {
        titleMap.set(String(lecturePage.id), title);
      }
    });

    return titleMap;
  }, [courseOutline, lecturePagesById]);

  const {
    activeQuestion,
    handleCloseDialog,
    onSubmit: submitStreamQuestion,
    resetStream,
    streamStatusMessage,
    streamingAnswer,
  } = useAskNovoAiStream({
    catalogId,
    lecturePageTitleById,
    isProcessingFilesInProgress,
  });

  const onSubmit = async () => {
    await submitStreamQuestion(formFields);
  };

  useEffect(() => {
    if (!askNovoAi.isOpen) {
      askNovoAiButtonRef.current?.focus();
    }
  }, [askNovoAi.isOpen]);

  useTrapFocus(expandedDrawerRef, askNovoAi.isOpen);

  const resetState = () => {
    setFormFields('');
    resetStream();
  };

  if (!shouldShowNovoAi) return null;

  if (!askNovoAi.isOpen) {
    // Collapsed state: small button in bottom right
    return (
      <NvTooltip
        text={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
        preventOverflow={false}
        enabled={displayMode === 'icon'}
      >
        <div css={askNovoAiContainer(isLecturePage, false)} className={containerClassName}>
          <button
            ref={askNovoAiButtonRef}
            css={novoAiCollapsedWrapper(displayMode)}
            onClick={() => dispatch(openNovoAIDialog())}
            type='button'
            aria-label={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
          >
            <div className='novoai-collapsed-icon'>
              <NvIcon icon='gen-ai' size='small' className='icon-gradient' />
              {displayMode === 'full' && (
                <span className='novoai-collapsed-label'>
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
                </span>
              )}
            </div>
          </button>
        </div>
      </NvTooltip>
    );
  }

  // Expanded state: drawer UI
  return (
    <div css={askNovoAiContainer(isLecturePage, true)}>
      <div
        ref={expandedDrawerRef}
        css={novoAiExpandedWrapper}
      >
        <div className='novoai-drawer-header'>
          <div className='novoai-header-left'>
            <NvIcon icon='gen-ai' size='small' className='icon-gradient' />
            <span className='novoai-title'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
            </span>
          </div>
          <button
            className='novoai-drawer-close'
            aria-label={t.NOVOED.CLOSE()}
            onClick={handleCloseDialog}
            type='button'
          >
            <NvIcon icon='arrow-up' size='xs-small' className='text-black' />
          </button>
        </div>
        <div className='novoai-drawer-content'>
          {askNovoAi.drawerState === AskNovoAiDrawerState.INITIAL && (
            <InitialDrawerState
              firstName={currentUser.firstName}
              formFields={formFields}
              setFormFields={setFormFields}
              onSubmit={onSubmit}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.LOADING && (
            <LoadingDrawerState
              question={activeQuestion || formFields}
              partialAnswer={streamingAnswer}
              statusMessage={streamStatusMessage}
              onCancel={resetState}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.SUCCESS && (
            <ResponseDrawerState
              question={activeQuestion || formFields || askNovoAi?.response?.questionText}
              onClick={resetState}
              isProcessingFilesInProgress={isProcessingFilesInProgress}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.ERROR && (
            <ErrorDrawerState
              question={activeQuestion || formFields}
              teachingTeam={helpOptions?.courseAdminUsers}
              onClick={resetState}
              setSendMessage={setSendMessage}
              isProcessingFilesInProgress={isProcessingFilesInProgress}
            />
          )}
        </div>
      </div>
      <NvModal
        type={ModalType.DYNAMIC}
        header={t.PROFILE.SEND_A_MESSAGE()}
        show={showSendMessage}
        onClose={() => setSendMessage(false)}
        doubleModal
        fullHeight={false}
        body={(
          <SendMessageModal
            subject={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NEED_HELP_MESSAGE()}
            body={activeQuestion || formFields}
            recipient={helpOptions?.courseAdminUsers
              ?.filter((item) => item.isQnaFacilitator)
              ?.map((user) => ({
                id: user.user.id,
                firstName: user.user.firstName,
                lastName: user.user.lastName,
                email: user.user.email,
                avatarUrl: '',
              })) || []}
            closeModal={() => setSendMessage(false)}
            skipDirtyCheck
          />
        )}
      />
    </div>
  );
};

export default AskNovoAI;
